// =============================================================================
// Back-office: trabajadores
// =============================================================================
import { apiClient } from './http';

export interface AdminUser {
  id: number;
  // Nullable: un trabajador de obra no tiene correo de empresa (ver
  // WorkerFormFields). Sin correo entra escaneando el QR de activación.
  email: string | null;
  first_name: string;
  last_name: string;
  role: 'ADMIN' | 'MANAGER' | 'EMPLOYEE';
  dni: string | null;
  employee_code: string | null;
  hire_date: string | null;
  weekly_hours: number;
  is_part_time: boolean;
  work_policy_id: number | null;
  shift_template_id: number | null;
  is_active: boolean;
  // Para saber si a un trabajador sin correo le queda pendiente activarse
  // (y por tanto si tiene sentido ofrecerle reemitir el QR): mismo criterio
  // que usa el backend en `POST /users/{id}/activation-qr/reissue`.
  is_verified: boolean;
  // Sólo viene en la respuesta del alta SIN correo, y sólo esa vez: el
  // panel lo convierte en QR (ActivationQr) y no se puede volver a pedir.
  activation_token?: string | null;
  // Sólo cuando esta respuesta pedía un envío de correo (invitación en el
  // alta, reinvitación al corregir el correo): si el correo SALIÓ de verdad.
  // El envío es best-effort (un SMTP caído no tumba el alta), pero el panel
  // tiene que poder decir «no se pudo enviar — reenvíalo desde su ficha»
  // en vez de darla por enviada.
  email_sent?: boolean | null;
}

export interface AdminUserCreate {
  // Opcional: un trabajador de obra no tiene correo de empresa.
  email?: string;
  // Sin password = invitación por email ("crea tu contraseña")
  password?: string;
  first_name?: string;
  last_name?: string;
  role: 'ADMIN' | 'MANAGER' | 'EMPLOYEE';
  dni?: string;
  employee_code?: string;
  hire_date?: string;
  weekly_hours?: number;
  is_part_time?: boolean;
  work_policy_id?: number;
  shift_template_id?: number;
}

export interface AdminUserUpdate {
  first_name?: string;
  last_name?: string;
  role?: 'ADMIN' | 'MANAGER' | 'EMPLOYEE';
  dni?: string;
  employee_code?: string;
  // Sólo se manda si trae valor: vacío = no tocar. Es la única vía de
  // corregirle la fecha a un trabajador creado sin ella (el alta ya la exige).
  hire_date?: string;
  weekly_hours?: number;
  is_part_time?: boolean;
  work_policy_id?: number;
  shift_template_id?: number;
  is_active?: boolean;
  password?: string;
}

export async function updateUser(id: number, data: AdminUserUpdate): Promise<AdminUser> {
  const res = await apiClient.patch<AdminUser>(`/users/${id}`, data);
  return res.data;
}

export async function getUsers(): Promise<AdminUser[]> {
  const res = await apiClient.get<AdminUser[]>('/users');
  return res.data;
}

export async function createUser(data: AdminUserCreate): Promise<AdminUser> {
  const res = await apiClient.post<AdminUser>('/users', data);
  return res.data;
}

/**
 * El PNG del QR de activación, como Blob.
 *
 * El QR lo genera el backend (mismo patrón que el QR de un centro), no una
 * librería en el navegador. El `token` lo trae quien llama esta función —
 * es el que ya llegó UNA vez en `AdminUser.activation_token` al dar de
 * alta — nunca se vuelve a pedir al servidor.
 */
export async function getActivationQr(userId: number, token: string): Promise<Blob> {
  const res = await apiClient.post(
    `/users/${userId}/activation-qr`,
    { token },
    { responseType: 'blob' },
  );
  return res.data as Blob;
}

/**
 * Reemitir el QR cuando el anterior se perdió sin escanear.
 *
 * Invalida el token vivo y emite uno nuevo en el propio backend — el panel
 * no ve ningún token, sólo el PNG resultante. Sólo tiene sentido para un
 * trabajador sin correo que todavía no ha activado (`!AdminUser.is_verified`).
 */
export async function reissueActivationQr(userId: number): Promise<Blob> {
  const res = await apiClient.post(
    `/users/${userId}/activation-qr/reissue`,
    undefined,
    { responseType: 'blob' },
  );
  return res.data as Blob;
}

/**
 * Reenviar la invitación por correo SIN cambiar el correo: reemite el
 * enlace (el anterior deja de valer) y lo manda de nuevo. Devuelve
 * `sent: false` (sin error HTTP) si el SMTP no logró entregarlo.
 */
export async function resendInvitation(
  userId: number,
): Promise<{ sent: boolean; email: string | null }> {
  const res = await apiClient.post(`/users/${userId}/resend-invitation`);
  return res.data;
}
